/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, March 21st 2022, 8:15:31 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { ActionDialog } from "ui-widgets/dialogs/GenericDialogs";
import { useToggleState } from "ui-widgets/dialogs/useToggleState";

/**
 * A component that allows for editing of a simple list where no complex editing features such as
 * dragging, filtering, selection is required. This simple editor can also work where the list
 * of items do not necessarily have a unique id property as it uses the index of the item in the array.
 * It also does not necessarily need to load items from an endpoint. It uses whatever items are
 * passed in the initialItems props
 *
 * For advanced list editor functionality use the ListEditorProvider and combine it with whichever components
 * you want to include in the editing process.
 *
 * Note: We will probably deprecate this over time as the platform grows.
 *
 */

enum LIST_STATE_ACTIONS {
  POPULATE = "populate",
  ADD = "add",
  EDIT = "edit",
  DELETE = "delete",
  SELECT = "select",
}

type ListReducerState<T> = { editableItems: T[]; currentEditItemIndex: number };

interface ListReducerActionPayLoad<T> {
  itemsToLoad?: T[];
  newObject?: T;
  objectIndex?: number;
}

interface ListReducerAction<T> {
  type: `${LIST_STATE_ACTIONS}`;
  payload: ListReducerActionPayLoad<T>;
}

type ListStateReducer<T> = (previousState: ListReducerState<T>, action: ListReducerAction<T>) => ListReducerState<T>;

const listStateReducer = <T,>(
  previousState: ListReducerState<T>,
  action: ListReducerAction<T>
): ListReducerState<T> => {
  switch (action.type) {
    case LIST_STATE_ACTIONS.POPULATE:
      return { ...previousState, editableItems: action.payload.itemsToLoad };

    case LIST_STATE_ACTIONS.ADD:
      return { ...previousState, editableItems: previousState.editableItems.concat(action.payload.newObject) };

    case LIST_STATE_ACTIONS.EDIT:
      return {
        ...previousState,
        editableItems: previousState.editableItems.map((obj, index) => {
          if (index === action.payload.objectIndex) {
            return action.payload.newObject;
          }
          return obj;
        }),
      };

    case LIST_STATE_ACTIONS.DELETE:
      return {
        ...previousState,
        editableItems: previousState.editableItems.filter((obj, index) => index !== action.payload.objectIndex),
      };

    case LIST_STATE_ACTIONS.SELECT:
      return { ...previousState, currentEditItemIndex: action.payload.objectIndex };
    default:
      throw new Error("Invalid Action for the List State Reducer of the simple list editor component");
  }
};

export interface EditListRenderPropSpec<T> {
  listState: ListReducerState<T>;
  currentEditItem: T;
  addNewItem: (item: T) => void;
  updateList: (item: T) => void;
  selectItemToEdit: (objectIndex: number) => void;
  removeItem: (objectIndex: number) => void;
}

export interface SimpleListEditorProps<T> {
  initialItems: T[];
  onUpdateList?: (updatedItemsList: T[]) => void;
  children: (args: EditListRenderPropSpec<T>) => JSX.Element;
}

export const SimpleListEditor = <T,>({ initialItems, onUpdateList, children }: SimpleListEditorProps<T>) => {
  const [listState, dispatch] = React.useReducer<ListStateReducer<T>>(listStateReducer, {
    editableItems: initialItems,
    currentEditItemIndex: null,
  });

  const selectItemToEdit = (objectIndex: number) => {
    dispatch({ type: LIST_STATE_ACTIONS.SELECT, payload: { objectIndex } });
  };

  const {
    handleClose: closeDeleteConfirmation,
    open: deleteConfirmationOpenState,
    handleOpen: openDeleteConfirmation,
  } = useToggleState();

  const [toDeletedIndex, setToDeleteIndex] = React.useState(null);

  const removeItem = (objectIndex: number) => {
    setToDeleteIndex(objectIndex);
    openDeleteConfirmation();
  };

  const confirmDelete = () => {
    if (toDeletedIndex === listState.currentEditItemIndex) selectItemToEdit(null);
    dispatch({ type: LIST_STATE_ACTIONS.DELETE, payload: { objectIndex: toDeletedIndex } });
    closeDeleteConfirmation();
  };

  const addNewItem = (item: T) => {
    dispatch({
      type: LIST_STATE_ACTIONS.ADD,
      payload: { newObject: item },
    });
  };

  //Caters for checking edit state even if object index is 0.
  const isItemEditState = typeof listState.currentEditItemIndex === "number";

  const updateList = (item: T) => {
    if (isItemEditState) {
      dispatch({
        type: LIST_STATE_ACTIONS.EDIT,
        payload: { objectIndex: listState.currentEditItemIndex, newObject: item },
      });
    } else {
      addNewItem(item);
    }
  };

  // Populate the items. Note that this caters for case when the initialItems could change
  // e.g. during a data fetch via an API call.
  React.useEffect(() => {
    if (initialItems.length > 0) {
      dispatch({
        type: LIST_STATE_ACTIONS.POPULATE,
        payload: { itemsToLoad: initialItems },
      });
    }
  }, [initialItems]);

  React.useEffect(() => {
    onUpdateList?.(listState.editableItems);
  }, [listState.editableItems, onUpdateList]);

  return (
    <>
      <DeleteConfirmationDialog
        deleteConfirmationOpenState={deleteConfirmationOpenState}
        closeDeleteConfirmation={closeDeleteConfirmation}
        confirmDelete={confirmDelete}
      />
      {children({
        listState,
        currentEditItem: isItemEditState ? listState.editableItems?.[listState.currentEditItemIndex] : null,
        updateList,
        selectItemToEdit,
        removeItem,
        addNewItem,
      })}
    </>
  );
};

const DeleteConfirmationDialog = ({ deleteConfirmationOpenState, closeDeleteConfirmation, confirmDelete }) => (
  <ActionDialog
    severity="warning"
    title="Deletion"
    open={deleteConfirmationOpenState}
    onClose={closeDeleteConfirmation}
    confirmationAction={confirmDelete}
    dialogMessage={<>Are you sure you want to delete this item?</>}
  />
);
